import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Button,
  cn,
  DataTable,
  DataTablePagination,
  ScrollArea,
  ScrollBar,
  SearchInput,
  V2Loader,
} from "web-design-system";
import {
  useSubmittedOrders,
  useRefetchTransactionStatus,
  useInvalidateTransactionOrder,
  useInvalidateSubmittedOrders,
} from "creso-core/hooks";
import { useCallback, useMemo, useState } from "react";
import { DownloadIcon, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { handleError } from "@/utils/error";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { isEmpty } from "lodash";
import { useDebounceCallback, useDebounceValue } from "usehooks-ts";
import { ORDER_STATUS_FILTER_MAP } from "creso-core/constants";
import { createOrdersColumns, NoData, OrderFilters } from "web-core";
import { OrderRowActions } from "./order-actions";

export function OrdersComponent() {
  const route = getRouteApi("/(auth)/_headed/clients/$id");
  const { client } = route.useLoaderData();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const portfolioRoute = getRouteApi("/(auth)/_headed/clients/$id/portfolio");
  const urlSearch = portfolioRoute.useSearch();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounceValue(search, 500);

  const {
    data: ordersData,
    error,
    isLoading,
    isPlaceholderData,
  } = useSubmittedOrders({
    investment_account_guid: client.investment_account ?? "",
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    status: urlSearch.order_status
      ?.map((s) => ORDER_STATUS_FILTER_MAP[s])
      .join(","),
    orderType: urlSearch.order_type?.join(","),
    productType: urlSearch.product_type?.join(","),
    search: debouncedSearch,
  });

  const updateSchemeNameSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setSearch(e.target.value);
    },
    []
  );

  const debouncedSchemeNameSearch = useDebounceCallback(
    updateSchemeNameSearch,
    300
  );

  const appliedFilters = useMemo(() => {
    const filters: Record<string, string[]> = {};
    if (urlSearch.order_status?.length) {
      filters.status = urlSearch.order_status;
    }
    if (urlSearch.order_type?.length) {
      filters.order_type = urlSearch.order_type;
    }
    if (urlSearch.product_type?.length) {
      filters.product_type = urlSearch.product_type;
    }
    return filters;
  }, [urlSearch.order_status, urlSearch.order_type, urlSearch.product_type]);

  const { mutateAsync: refetchTransactionStatus } = useRefetchTransactionStatus(
    client.investment_account ?? ""
  );

  const [isRefetchingTransactions, setIsRefetchingTransactions] =
    useState(false);

  const { invalidate: invalidateTransactionOrder } =
    useInvalidateTransactionOrder(client.investment_account ?? "");

  const invalidateSubmittedOrders = useInvalidateSubmittedOrders(
    client?.investment_account ?? ""
  );

  const refreshTransactionStatus = useCallback(async () => {
    try {
      setIsRefetchingTransactions(true);
      await refetchTransactionStatus();
      // Following lines are to makes sure recently failed orders are
      // recalculated when orders are refreshed.
      // recently failed orders banner is shown only when there are no
      // draft orders, so draft orders needs to be invalidated here.
      invalidateSubmittedOrders();
      invalidateTransactionOrder();
      toast.success("Orders updated successfully");
    } catch (error) {
      handleError(error, "Failed to refresh transaction status");
    } finally {
      setIsRefetchingTransactions(false);
    }
  }, [
    refetchTransactionStatus,
    invalidateSubmittedOrders,
    invalidateTransactionOrder,
  ]);

  const columns = useMemo(
    () =>
      createOrdersColumns({
        renderSchemeLink: ({ fundId, children }) => (
          <Link to="/funds/$fund_id" params={{ fund_id: fundId }}>
            {children}
          </Link>
        ),
        renderActions: (order) => (
          <OrderRowActions order={order} client={client} />
        ),
      }),
    [client]
  );

  const table = useReactTable({
    data: ordersData?.results ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
    manualPagination: true,
    getPaginationRowModel: getPaginationRowModel(),
    rowCount: ordersData?.count,
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  });

  const noResults =
    (error || !ordersData || ordersData?.results.length === 0) &&
    isEmpty(appliedFilters) &&
    !search;

  return (
    <V2Loader isLoading={isLoading} className="flex-1 gap-4">
      {noResults ? (
        <NoData
          className="p-32"
          title={"No Orders Yet"}
          description="Get them started. Orders populate here."
        />
      ) : (
        <>
          <div className="flex flex-row items-center gap-3">
            <div className="w-70 mr-auto">
              <SearchInput
                placeholder="Search by scheme name"
                onChange={debouncedSchemeNameSearch}
              />
            </div>
            <Button
              variant="outline"
              className={cn(isEmpty(appliedFilters) && noResults && "hidden")}
              onClick={() =>
                navigate({
                  to: ".",
                  search: (prev) => ({
                    ...prev,
                    modal: "order-history",
                  }),
                })
              }
            >
              <DownloadIcon />
              Order history
            </Button>
            <OrderFilters
              value={{
                order_status: urlSearch.order_status,
                order_type: urlSearch.order_type,
                product_type: urlSearch.product_type,
              }}
              onChange={(filters) => {
                setPagination((pagination) => ({
                  ...pagination,
                  pageIndex: 0,
                }));
                navigate({
                  to: ".",
                  replace: true,
                  search: (prev: Record<string, unknown>) => ({
                    ...prev,
                    order_status: filters?.order_status ?? [],
                    order_type: filters?.order_type ?? [],
                    product_type: filters?.product_type ?? [],
                  }),
                });
              }}
              className={cn(
                isEmpty(appliedFilters) && noResults && "hidden",
                "relative"
              )}
            />
            <Button
              size="icon"
              className={cn(isEmpty(appliedFilters) && noResults && "hidden")}
              onClick={refreshTransactionStatus}
              variant="outline"
            >
              <RefreshCw
                className={cn(isRefetchingTransactions && "animate-spin")}
              />
            </Button>
          </div>
          <div className="flex flex-col flex-1 w-full overflow-hidden border border-border rounded-lg">
            <ScrollArea className="flex-1 flex flex-col">
              <DataTable
                className={cn(
                  table.getFilteredRowModel().rows.length === 0 && "flex-1",
                  "border-0 flex-1"
                )}
                disabled={isPlaceholderData}
                pagination={false}
                table={table}
              />
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
            <DataTablePagination
              table={table}
              className="sticky bottom-0 z-20"
            />
          </div>
        </>
      )}
    </V2Loader>
  );
}
